import { useEffect, useRef } from 'react'
import {
  differenceInMinutes,
  startOfDay,
  parseISO,
  startOfWeek,
  isSameDay,
  isToday,
} from 'date-fns'
import { CalendarEvent } from '../AppointmentTypes'

interface WeekProps {
  currentDate: Date
  events: CalendarEvent[]
}

const Week: React.FC<WeekProps> = ({ currentDate, events }) => {
  const container = useRef<HTMLDivElement>(null)

  const rowHeight = 40 // px (each row = 15 minutes)

  useEffect(() => {
    if (container.current) {
      container.current.scrollTop = 8 * 4 * rowHeight // Scroll to 8am
    }
  }, [])

  const getWeekDates = (date: Date) => {
    const start = startOfWeek(date, { weekStartsOn: 0 })
    return Array.from(
      { length: 7 },
      (_, i) =>
        new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    )
  }

  const weekDates = getWeekDates(currentDate)

  const renderEvents = () => {
    const eventsByDay: Record<number, CalendarEvent[]> = {}
    weekDates.forEach((_, index) => (eventsByDay[index] = []))

    events.forEach(event => {
      const start = parseISO(event.start.date_time)
      const dayIndex = weekDates.findIndex(day => isSameDay(day, start))
      if (dayIndex !== -1) {
        eventsByDay[dayIndex].push(event)
      }
    })

    const renderedEvents: JSX.Element[] = []

    Object.entries(eventsByDay).forEach(([dayIndexStr, dayEvents]) => {
      const dayIndex = parseInt(dayIndexStr)

      // Sort events by start time
      const sorted = [...dayEvents].sort(
        (a, b) =>
          parseISO(a.start.date_time).getTime() -
          parseISO(b.start.date_time).getTime()
      )

      const overlappingGroups: CalendarEvent[][] = []

      sorted.forEach(event => {
        const start = parseISO(event.start.date_time)
        let placed = false
        for (const group of overlappingGroups) {
          const last = parseISO(group[group.length - 1].end.date_time)
          if (start >= last) {
            group.push(event)
            placed = true
            break
          }
        }
        if (!placed) overlappingGroups.push([event])
      })

      overlappingGroups.forEach((group, groupIndex, allGroups) => {
        group.forEach((event, i) => {
          const start = parseISO(event.start.date_time)
          const end = parseISO(event.end.date_time)

          const startMins = differenceInMinutes(start, startOfDay(start))
          const duration = Math.max(differenceInMinutes(end, start), 15)

          const topOffset = (startMins / 15) * rowHeight
          const height = (duration / 15) * rowHeight

          const totalCols = allGroups.length
          const leftOffsetPercent = (groupIndex / totalCols) * 100
          const widthPercent = 100 / totalCols

          renderedEvents.push(
            <div
              key={event.id}
              className="absolute mx-0.5 rounded-lg bg-blue-100 px-2 py-1 text-xs shadow-md"
              style={{
                gridColumn: `${dayIndex + 1} / span 1`,
                top: `${topOffset}px`,
                height: `${height}px`,
                left: `${leftOffsetPercent}%`,
                width: `${widthPercent}%`,
              }}
            >
              <p className="font-semibold text-blue-700 truncate">
                {event.title}
              </p>
              <p className="text-blue-500">
                {start.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                -{' '}
                {end.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          )
        })
      })
    })

    return renderedEvents
  }

  return (
    <div
      ref={container}
      className="relative isolate h-[600px] overflow-auto bg-white"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 grid grid-cols-8 bg-white border-b text-sm font-medium">
        <div className="col-span-1" />
        {weekDates.map((date, i) => (
          <div key={i} className="flex items-center justify-center py-3">
            <span
              className={`flex items-baseline gap-1 ${isToday(date) ? 'text-blue-600 font-bold' : 'text-gray-900'}`}
            >
              {date.toLocaleDateString('en-US', { weekday: 'short' })}
              <span
                className={`size-6 flex items-center justify-center rounded-full ${isToday(date) ? 'bg-blue-600 text-white' : ''} ${date.getDate().toString().length == 2 && isToday(date) ? 'text-xs' : ''}`}
              >
                {date.getDate()}
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* Time Grid */}
      <div className="relative grid grid-cols-8">
        {/* Times */}
        <div className="col-span-1 divide-y">
          {Array.from({ length: 24 }).map((_, i) => (
            <div
              key={i}
              className="h-[160px] text-right pr-2 text-xs text-gray-400"
            >
              {i === 0
                ? '12AM'
                : i < 12
                  ? `${i}AM`
                  : i === 12
                    ? '12PM'
                    : `${i - 12}PM`}
            </div>
          ))}
        </div>

        {/* Grid + Events */}
        <div
          className="col-span-7 relative grid grid-cols-7 border-l border-t divide-x divide-y divide-gray-200"
          style={{ gridTemplateRows: `repeat(96, ${rowHeight}px)` }}
        >
          {/* Filler grid cells to make lines visible */}
          {Array.from({ length: 96 * 7 }).map((_, i) => (
            <div key={i} className="h-full w-full" />
          ))}
          {renderEvents()}
        </div>
      </div>
    </div>
  )
}

export default Week
